<template lang="pug">
.meetSidebar.w-100
  // Mapa móvil sticky SOLO en mobile
  .meetSidebar__mobileMap
    .map-box
      img(src="/images/ukMap.svg", alt="Map")

  .meetSidebar__items.d-flex.flex-column.gap-2.w-100
    EcreativMoleculesMeetForm(
      v-if="!hasSearched"
      heading="Reach Out to an Imam"
      description="Please provide your details to search for your local Imam. You’ll be able to choose how to contact them in the next step."
      :loading="loadingImams"
      @find="handleSearch"
    )
    .d-flex.flex-column.gap-1(v-else)
      EcreativArchetypesHeading.mb-1(tag="h4" size="5" context="dark") {{ headerText }}
      EcreativArchePrimitivesNavigationsInteractive.d-flex.text-capitalize.gap-1.mb-2(@click="resetSearch")
        template(#icon)
          EcreativAtomsSvgIcon(iconName="tabler:edit" iconSize="xl")
          | Change Postcode

    EcreativMoleculesMeetProfileInformation(
      v-for="im in displayedImams"
      :key="im.id"
      :location="im"
      :open-id="openId"
      @toggle-open="onToggleOpen"
      @send-message="onSendMessage"
      @request-callback="onRequestCallback"
      v-if="hasSearched"
    )

    //- v-if="hasSearched && totalImams > displayedCount"
    EcreativArchetypesInteractiveArchetype.meetSidebar__button.w-100.mt-3.justify-content-center(
      v-if="false"
      componentType="FakeInteractive"
      :text="`Load More (${remainingImams} remaining)`"
      context="dark"
      @click="loadMore"
    )

    Teleport(
      v-if="showCallbackModal && activeTeleportTarget"
      :to="activeTeleportTarget"
    )
      .card-overlay(role="dialog" aria-modal="true" @click.self="showCallbackModal = false")
        .popup-content
          EcreativMoleculesRequestCallbackModal(
            :heading="`Request a Call Back from ${activeImam?.name_to_display ?? ''}`"
            :subTitle="messageSubTitle"
            description="We'll pass your phone number to the Imam. We'll never share your information with anyone else, and you'll hear from him as soon as possible."
            placeholder="Your Phone Number"
            v-model="callbackPhone"
            :show="true"
            :loading="submittingCallback"
            :contactTitle="contact.title"
            :contactName="contact.name"
            :contactEmail="contact.email"
            :contactPostcode="contact.postcode"
            :imamId="activeImam?.id ?? ''"
            :imamName="activeImam?.name_to_display ?? ''"
            :distance="activeImam?.distance ?? ''"
            @close="showCallbackModal = false"
          )

    Teleport(
      v-if="showMessageModal && activeTeleportTarget"
      :to="activeTeleportTarget"
    )
      .card-overlay(role="dialog" aria-modal="true" @click.self="showMessageModal = false")
        .popup-content
          EcreativMoleculesSendMessageModal(
            :show="true"
            :loading="submittingMessage"
            :heading="messageHeading"
            :subTitle="messageSubTitle"
            :description="messageDescription"
            placeholder="Type your message here..."
            v-model="messageText"
            :contactTitle="contact.title"
            :contactName="contact.name"
            :contactEmail="contact.email"
            :contactPostcode="contact.postcode"
            :imamId="activeImam?.aims ?? ''"
            :imamName="activeImam?.name_to_display ?? ''"
            :distance="activeImam?.distance ?? ''"
            @close="showMessageModal = false"
          )
    EcreativMoleculesErrorMessage(:error="errorImams")
</template>

<script setup lang="ts">
/* eslint-disable */
import { ref, reactive, computed, nextTick, watch } from 'vue';

interface Imam {
  id: string | number;
  name: string;
  name_to_display: string;
  aims: string;
  department?: string;
  location?: string;
  distance?: number | string;
  introduction?: string;
  expectText?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  postcode?: string;
  associated_mosque?: string;
}

const imams = ref<unknown>([]);
const loadingImams = ref(false);
const errorImams = ref('');
const searchedPostcode = ref('');
const displayedCount = ref(3);

/* Accordion state */
const openId = ref<string | number | null>(null);

/* Contact details captured from the first form */
const contact = reactive({
  title: '',
  name: '',
  email: '',
  postcode: '',
});

/* Callback modal */
const showCallbackModal = ref(false);
const submittingCallback = ref(false);
const callbackPhone = ref('');

/* Send message modal */
const showMessageModal = ref(false);
const submittingMessage = ref(false);
const messageText = ref('');

/* Teleport anchor */
const activeLocationId = ref<string | number | null>(null);
const activeTeleportTarget = computed(() =>
  activeLocationId.value != null ? `#card-popup-${activeLocationId.value}` : '',
);

/* Normalize data safely */
const imamsArray = computed<Imam[]>(() => {
  const v = imams.value as any;
  if (Array.isArray(v)) return v as Imam[];
  if (v && Array.isArray(v.results)) return v.results as Imam[];
  if (v && Array.isArray(v.data)) return v.data as Imam[];
  if (v && Array.isArray(v.items)) return v.items as Imam[];
  if (v && typeof v === 'object' && 'id' in v) return [v as Imam];
  return [];
});

const displayedImams = computed(() =>
  imamsArray.value.slice(0, displayedCount.value),
);
const totalImams = computed(() => imamsArray.value.length);
const remainingImams = computed(() =>
  Math.max(totalImams.value - displayedCount.value, 0),
);
const headerText = computed(
  () => `Closest Imams near: ${searchedPostcode.value}`,
);
const hasSearched = computed(() => searchedPostcode.value !== '');

const activeImam = computed<Imam | null>(() => {
  const id = activeLocationId.value;
  if (id == null) return null;
  return imamsArray.value.find((i) => String(i.id) === String(id)) || null;
});

const messageHeading = computed(() =>
  activeImam.value
    ? `Send Message to ${activeImam.value.name_to_display}`
    : 'Send Message',
);
const messageSubTitle = computed(() => activeImam.value?.location || '');

const messageDescription = computed(
  () =>
    'We’ll pass your message directly to the Imam and reply using the email you provided in Step 1. We’ll never share your information with anyone else, and you’ll hear from us as soon as possible.',
);

/* Keep openId valid if list changes */
watch(imamsArray, () => {
  if (openId.value == null) return;
  const stillExists = imamsArray.value.some(
    (i) => String(i.id) === String(openId.value),
  );
  if (!stillExists) openId.value = null;
});

/* Fetch */
async function fetchImams(postcode: string): Promise<unknown> {
  const resp = await fetch('/api/search-imams', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ postcode }),
  });
  if (!resp.ok) throw new Error('Please enter a valid postcode.');
  return await resp.json();
}

async function handleSearch(payload: {
  title: string;
  name: string;
  email: string;
  postcode: string;
}) {
  const pc = payload.postcode?.trim?.() ?? '';
  if (!pc) {
    errorImams.value = 'Please enter a valid postcode.';
    return;
  }

  /* Capture contact details to forward to modals */
  contact.title = payload.title?.trim?.() ?? '';
  contact.name = payload.name?.trim?.() ?? '';
  contact.email = payload.email?.trim?.() ?? '';
  contact.postcode = pc;

  searchedPostcode.value = pc;
  loadingImams.value = true;
  errorImams.value = '';
  try {
    imams.value = await fetchImams(pc);
  } catch (e: any) {
    errorImams.value = e?.message || 'Unexpected error';
    imams.value = [];
  } finally {
    loadingImams.value = false;
  }
}

function onToggleOpen(id: string | number) {
  const next = openId.value === id ? null : id;

  if (showMessageModal.value || showCallbackModal.value) {
    showMessageModal.value = false;
    showCallbackModal.value = false;
    messageText.value = '';
    callbackPhone.value = '';
  }

  openId.value = next;
  activeLocationId.value = next;
}

function loadMore() {
  displayedCount.value = Math.min(displayedCount.value + 3, totalImams.value);
}

function onSendMessage(im: Imam) {
  openId.value = im.id;
  activeLocationId.value = im.id;

  showCallbackModal.value = false;
  showMessageModal.value = true;

  nextTick(() => {
    const el = document.querySelector(activeTeleportTarget.value);
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  });
}

function onRequestCallback(im: Imam) {
  openId.value = im.id;
  activeLocationId.value = im.id;

  showMessageModal.value = false;
  showCallbackModal.value = true;

  nextTick(() => {
    const el = document.querySelector(activeTeleportTarget.value);
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  });
}

function resetSearch() {
  searchedPostcode.value = '';
  imams.value = [];
  errorImams.value = '';
  callbackPhone.value = '';
  messageText.value = '';
  openId.value = null;
  activeLocationId.value = null;
  contact.postcode = '';
}
</script>

<style lang="scss" scoped>
.meetSidebar {
  /* 100px para esquivar el header sticky; pull-up controla cuánto sube el form */
  --sticky-offset: 100px;
  --pull-up: clamp(32px, 12vh, 160px);
}

/* Sticky map solo en móvil */
.meetSidebar__mobileMap {
  position: sticky;
  top: var(--sticky-offset);
  z-index: 0;
  display: block;
  height: calc(100svh - var(--sticky-offset) - var(--pull-up));

  .map-box {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .map-box > * {
    position: absolute;
    display: block;
    object-fit: cover;
    width: 100%;
    height: 100%;
    inset: 0;
  }

  @include media-breakpoint-up(md) {
    display: none;
  }

  @supports not (height: 100svh) {
    height: calc(100vh - var(--sticky-offset) - var(--pull-up));
  }
}

/* Form encima del mapa móvil */
.meetSidebar__items {
  position: relative;
  z-index: 1;

  @include media-breakpoint-up(md) {
    background: transparent;
  }
}

/* Overlays */
.card-overlay {
  position: absolute;
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: rgb(0 0 0 / 80%);
  inset: 0;
  border-radius: 1rem;
}

.popup-content {
  overflow: auto;
  width: 100%;
  height: 100%;
  animation: popup-appear 0.2s ease-out;
  border-radius: inherit;
}

@keyframes popup-appear {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
